import React from "react";
import { NavLink } from "react-router-dom";
import { useConfig } from "../context/ConfigContext.jsx";
import { useTheme } from "../context/ThemeContext.jsx";
import { useCompanies } from "../../features/companies/hooks/useCompanies.js";
import { ROUTES, NAV_SECTIONS, isRouteEnabled } from "../../app/routeConfig.js";
import { comboHint } from "../shortcuts/registry.js";

/** Desktop sidebar (≥1024px): every enabled page grouped by section; collapses to an icon rail. */
export function SideNav({ collapsed, onToggle, onShowHelp }) {
  const config = useConfig();
  const { theme, toggleTheme } = useTheme();
  const { defaultCompany } = useCompanies();
  const features = config?.features || {};
  const enabledRoutes = ROUTES.filter((route) => isRouteEnabled(route, features));

  return (
    <nav className={`side-nav ${collapsed ? "collapsed" : ""}`} aria-label="Main">
      <div className="side-nav-brand">
        {defaultCompany?.logoUrl ? (
          <img src={defaultCompany.logoUrl} alt="" className="side-nav-logo-img" />
        ) : (
          <span className="side-nav-logo">{config?.branding?.appLogo || "💼"}</span>
        )}
        {!collapsed && (
          <div className="side-nav-brand-text">
            <div className="side-nav-brand-name" title={defaultCompany?.name}>{defaultCompany?.name || config?.name || "Books"}</div>
            <div className="side-nav-brand-sub">{config?.name || "Books & Inventory"}</div>
          </div>
        )}
        <button
          type="button"
          className="side-nav-collapse"
          onClick={onToggle}
          title={`${collapsed ? "Expand" : "Collapse"} menu (${comboHint("toggle_sidebar")})`}
          aria-label={collapsed ? "Expand menu" : "Collapse menu"}
        >
          {collapsed ? "»" : "«"}
        </button>
      </div>

      <div className="side-nav-menu">
        {NAV_SECTIONS.map((section) => {
          const items = enabledRoutes.filter((route) => route.section === section);
          if (!items.length) return null;
          return (
            <div key={section} className="side-nav-section">
              {collapsed ? <div className="side-nav-divider" /> : <div className="side-nav-section-title">{section}</div>}
              {items.map((route) => (
                <NavLink
                  key={route.id}
                  to={route.path}
                  end={route.path === "/"}
                  title={collapsed ? route.label : undefined}
                  className={({ isActive }) => `side-nav-item ${isActive ? "active" : ""}`}
                >
                  <span className="side-nav-icon">{route.icon}</span>
                  {!collapsed && <span className="side-nav-label">{route.label}</span>}
                </NavLink>
              ))}
            </div>
          );
        })}
      </div>

      <div className="side-nav-footer">
        <button
          type="button"
          className="side-nav-item"
          onClick={toggleTheme}
          title={`Switch theme (${comboHint("toggle_theme")})`}
        >
          <span className="side-nav-icon">{theme === "light" ? "🌙" : "☀️"}</span>
          {!collapsed && <span className="side-nav-label">{theme === "light" ? "Dark mode" : "Light mode"}</span>}
        </button>
        <button type="button" className="side-nav-item" onClick={onShowHelp} title="Keyboard shortcuts (?)">
          <span className="side-nav-icon">⌨️</span>
          {!collapsed && <span className="side-nav-label">Shortcuts</span>}
          {!collapsed && <kbd className="kbd-chip side-nav-kbd">?</kbd>}
        </button>
      </div>
    </nav>
  );
}

export default SideNav;
